// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Single source of truth for device health status logic.
 *
 * Canonical DB values (written by the backend):
 *   "green"  – fully connected (MQTT + HTTP)
 *   "yellow" – degraded (HTTP-reachable, MQTT stale)
 *   "red"    – offline / unreachable
 *
 * Legacy values ("online", "offline") may still exist in the DB during
 * transition; the helpers below accept them transparently.
 */

const CANONICAL = {
  green: 'green',
  online: 'green',
  yellow: 'yellow',
  degraded: 'yellow',
  warning: 'yellow',
  mqtt_stale: 'yellow',
  red: 'red',
  offline: 'red',
};

/** Normalise any raw health_status string to a canonical value. */
export function normalizeHealth(raw) {
  return CANONICAL[(raw || '').toLowerCase().trim()] || 'red';
}

/** Is the device considered reachable (green or yellow)? */
export function isDeviceOnline(raw) {
  const h = normalizeHealth(raw);
  return h === 'green' || h === 'yellow';
}

/** Is the device fully healthy (green only)? */
export function isDeviceHealthy(raw) {
  return normalizeHealth(raw) === 'green';
}

/** MUI Chip `color` prop value. */
export function getHealthChipColor(raw) {
  const MAP = { green: 'success', yellow: 'warning', red: 'error' };
  return MAP[normalizeHealth(raw)] || 'error';
}

/** Human-readable label for the status. */
export function getHealthLabel(raw) {
  const LABELS = { green: 'Online', yellow: 'Degraded', red: 'Offline' };
  return LABELS[normalizeHealth(raw)] || 'Offline';
}

/** CSS class for the pulsing health indicator dot. */
export function getHealthIndicatorClass(raw) {
  const CLASSES = {
    green: 'health-green pulse-green',
    yellow: 'health-yellow pulse-yellow',
    red: 'health-red pulse-red',
  };
  return CLASSES[normalizeHealth(raw)] || CLASSES.red;
}
